"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SearchCheck, FileCode2, Globe as Globe2, Star, Cpu, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "Comprehensive SEO Audit",
  description: "I conduct a deep technical and structural analysis of your site to uncover hidden crawling bottlenecks, code issues, and untapped organic growth opportunities.",
  deliverable: "Technical & On-Page Roadmap",
  iconName: "SearchCheck"
}, {
  number: "02",
  title: "Strategic Optimization",
  description: "I restructure core landing pages, optimize content intent, and refine internal architecture so search engines and visitors immediately understand your value.",
  deliverable: "Content & Keyword Hierarchy",
  iconName: "FileCode2"
}, {
  number: "03",
  title: "Citations & Local Authority",
  description: "For local, national, or international markets, I optimize citation signals, Google Business Profiles, and structured entity references to build localized trust.",
  deliverable: "Clean Citation & Signal Profile",
  iconName: "Globe2"
}, {
  number: "04",
  title: "Review & Reputation Growth",
  description: "Digital trust directly impacts rankings. I guide you in building structured review systems and sentiment signals across key industry platforms.",
  deliverable: "Reputation Strategy Framework",
  iconName: "Star"
}, {
  number: "05",
  title: "Schema & AI Search Optimization",
  description: "I deploy advanced JSON-LD structured data and entity relationships so your brand stands out in Google SGE, ChatGPT answers, and AI engines.",
  deliverable: "Full Schema Deployment",
  iconName: "Cpu"
}, {
  number: "06",
  title: "Transparent Reporting & Iteration",
  description: "No jargon or vanity metrics. You get direct monthly video walkthroughs, plain-English KPI tracking, and proactive adjustments directly from me.",
  deliverable: "Monthly Performance Dashboard",
  iconName: "BarChart3"
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "SearchCheck":
        return <SearchCheck className="size-6 text-primary" />;
      case "FileCode2":
        return <FileCode2 className="size-6 text-primary" />;
      case "Globe2":
        return <Globe2 className="size-6 text-primary" />;
      case "Star":
        return <Star className="size-6 text-primary" />;
      case "Cpu":
        return <Cpu className="size-6 text-primary" />;
      case "BarChart3":
        return <BarChart3 className="size-6 text-primary" />;
      default:
        return <CheckCircle2 className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologyprocessflow" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-1/4 left-0 -z-10 h-72 w-72 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-0 -z-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 px-3 py-1 font-medium text-xs tracking-wide">
            <Sparkles className="size-3.5 mr-1 text-primary" />
            Transparent SEO Methodology
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight mb-6">
            A Step-by-Step Flow Designed for Sustainable Growth
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When you hire me, you get a direct partner who executes a structured,
            battle-tested SEO process. No agency hand-offs, no fluff — just
            clear execution focused on measurable results.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {steps.map((step, i) => <motion.div key={step.number} data-index={i} variants={itemVariants} className="relative group h-full">
              {}
              <div className="absolute -top-3 right-6 z-10">
                <span className="inline-flex items-center justify-center font-mono font-bold text-xs bg-accent text-accent-foreground rounded-full px-3 py-1 shadow-xs border border-accent/40">
                  Step {step.number}
                </span>
              </div>

              <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 relative overflow-hidden flex flex-col justify-between pt-8">
                {}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-accent" />

                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                        {getStepIcon(step.iconName)}
                      </div>
                      <span className="text-3xl font-bold font-serif text-primary/20 group-hover:text-primary/40 transition-colors">
                        {step.number}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground tracking-tight">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </CardDescription>

                    {}
                    <div className="pt-2">
                      <div className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground/80 bg-muted px-2.5 py-1 rounded-md border border-border">
                        <CheckCircle2 className="size-3.5 text-accent" />
                        <span>Deliverable: {step.deliverable}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {}
        <div className="mt-16 lg:mt-20 rounded-2xl bg-muted/60 border border-border p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-48 h-48 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10 max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
              Ready to execute a customized SEO roadmap for your business?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mb-8 leading-relaxed">
              Book a direct consultation with me. We will discuss your current organic positioning and map out the exact strategy needed for your market.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto font-medium">
                <Link to="/CaseStudiesPage">
                  Explore Verified Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}